import { useCallback, useMemo } from "react";

import useBetaFeatures, { BETA_FEATURES, BetaFeatures } from "./useBetaFeatures";
import useFeatureFlags, { FEATURE_FLAGS, FeatureFlag } from "./useFeatureFlags";

const FEATURE_FLAG_SET = new Set<string>(FEATURE_FLAGS);
const BETA_FEATURE_SET = new Set<string>(BETA_FEATURES);

// Dev-only dedupe: hasFeature is invoked per-widget, per-render, so a single
// unknown name would otherwise flood the console every render. Track names
// we've already warned about (module-level, so it survives across hook
// instances/re-renders) and only warn once per name.
const warnedUnknownNames = new Set<string>();

/**
 * Combined gate resolver for names that arrive as plain strings (e.g. from
 * the workspace GraphQL API) and could be either a long-lived feature flag
 * or an opt-in beta feature.
 *
 * Routing:
 * - If `name` is a known `FeatureFlag` -> inverted, default-true check via
 *   `useFeatureFlags` (`organization.disabledFeatures`).
 * - Otherwise -> existing opt-in `betaFeatures` check via `useBetaFeatures`.
 *
 * @returns {Object} An object with a `hasFeature` method to check if a name is enabled.
 */
export default function useFeatureGate() {
  const { hasFeature: hasFlag } = useFeatureFlags();
  const { hasFeature: hasBeta } = useBetaFeatures();

  // hasFlag/hasBeta are now stable across renders (useFeatureFlags/
  // useBetaFeatures memoize them on disabledFeatures/betaFeatures via
  // useCallback), so this useCallback genuinely memoizes `hasFeature` too —
  // it only changes when the org's flag/beta-feature set changes.
  const hasFeature = useCallback(
    (name: string): boolean => {
      if (FEATURE_FLAG_SET.has(name)) {
        return hasFlag(name as FeatureFlag);
      }
      // Dev-only: a name that's neither a known FeatureFlag nor a known
      // BetaFeatures falls through to the (opt-in, default-false) beta
      // check below, which silently hides the surface for every org — e.g.
      // a flag seeded into workspace config before the web-ui constant that
      // names it has shipped. Warn so this doesn't go unnoticed in dev.
      if (
        process.env.NODE_ENV !== "production" &&
        !BETA_FEATURE_SET.has(name) &&
        !warnedUnknownNames.has(name)
      ) {
        warnedUnknownNames.add(name);
        // eslint-disable-next-line no-console
        console.warn(
          `useFeatureGate: "${name}" is not a known FeatureFlag or BetaFeatures name — falling back to the beta check (defaults to disabled).`
        );
      }
      return hasBeta(name as BetaFeatures);
    },
    [hasFlag, hasBeta]
  );

  // Mirror useFeatureFlags/useBetaFeatures: memoize the returned object on
  // hasFeature so consumers that depend on the returned object identity
  // (e.g. in a useEffect/useMemo/useCallback deps array) don't recompute on
  // every render.
  return useMemo(() => ({ hasFeature }), [hasFeature]);
}
